<template>
  <div>
    <advertisement-module
      v-if="advertisementVisible"
      :duration="advertisementDuration"
      :graphic="advertisementGraphic"
      :windowInnerHeight="windowInnerHeight"
      @expire="showHome"
    />
    <home-module
      v-else-if="homeVisible"
      :title="clientName"
      :options="priceListOptions"
      :clientLogo="clientLogo"
      :brandLogo="brandLogo"
      :windowInnerHeight="windowInnerHeight"
      @select="showPriceList"
    />
    <price-list-module
      v-else-if="priceListVisible"
      :pages="priceListPages"
      :windowInnerHeight="windowInnerHeight"
      @back="showHome"
    />
  </div>
</template>

<script>
import AdvertisementModule from "./modules/AdvertisementModule";
import HomeModule from "./modules/HomeModule";
import PriceListModule from "./modules/PriceListModule";
import { MODULE_TYPE_ENUM } from "./common/vo/ModuleTypeEnum";
import { PRICE_LIST_TYPE_ENUM } from "./common/vo/PriceListTypeEnum";
import dayjs from "dayjs";
import pageLifecycle from "page-lifecycle/dist/lifecycle.es5";
import { PAGE_LIFECYCLE_STATE_ENUM } from "./common/vo/PageLifecycleStateEnum";
import { preloadImages } from "./common/library/preload";

const REFRESH_TIMEOUT_S = 300;

export default {
  name: "App",

  components: { AdvertisementModule, HomeModule, PriceListModule },

  data() {
    return {
      activeModule: MODULE_TYPE_ENUM.advertisement,
      advertisementEnabled: this.$cchbc.adEnabled,
      advertisementDuration: this.$cchbc.adDuration,
      backgroundColor: this.$cchbc.backgroundColor,
      clientName: this.$cchbc.clientName,
      priceListType: PRICE_LIST_TYPE_ENUM.default,
      windowInnerHeight: undefined,
      lastVisible: null,
    };
  },

  computed: {
    advertisementAvailable() {
      return (
        this.advertisementEnabled &&
        this.advertisementDuration > 0 &&
        this.advertisementGraphic
      );
    },

    advertisementVisible() {
      return this.activeModule === MODULE_TYPE_ENUM.advertisement;
    },

    homeVisible() {
      return this.activeModule === MODULE_TYPE_ENUM.home;
    },

    priceListVisible() {
      return this.activeModule === MODULE_TYPE_ENUM.priceList;
    },

    priceListOptions() {
      const options = [];

      if (this.promoPages.length > 0) {
        options.push({
          type: PRICE_LIST_TYPE_ENUM.promo,
          label: this.$cchbc.promoLabel,
        });
      }

      if (this.pages.length > 0) {
        options.push({
          type: PRICE_LIST_TYPE_ENUM.default,
          label: this.$cchbc.defaultLabel,
        });
      }

      return options;
    },

    priceListPages() {
      switch (this.priceListType) {
        case PRICE_LIST_TYPE_ENUM.default:
          return this.pages;
        case PRICE_LIST_TYPE_ENUM.promo:
          return this.promoPages;
        default:
          return [];
      }
    },

    advertisementGraphic() {
      return (
        this.$cchbc &&
        this.$cchbc.adGraphic &&
        this.$cchbc.adGraphic.downloadUrl
      );
    },

    clientLogo() {
      return this.$cchbc && this.$cchbc.logo && this.$cchbc.logo.downloadUrl;
    },

    brandLogo() {
      return (
        this.$cchbc &&
        this.$cchbc.brandLogo &&
        this.$cchbc.brandLogo.downloadUrl
      );
    },

    pages() {
      return (this.$cchbc && this.$cchbc.pages) || [];
    },

    promoPages() {
      return (this.$cchbc && this.$cchbc.promoPages) || [];
    },
  },

  watch: {
    activeModule(newValue) {
      if (newValue === MODULE_TYPE_ENUM.priceList) {
        document.body.style.backgroundColor = this.backgroundColor;
      } else {
        document.body.style.backgroundColor = "";
      }
    },
  },

  created() {
    preloadImages([
      this.clientLogo,
      this.brandLogo,
      this.pages && this.pages[0] && this.pages[0].downloadUrl,
      this.promoPages && this.promoPages[0] && this.promoPages[0].downloadUrl,
    ]);
  },

  mounted() {
    this.initializeWindowInnerHeight();
    this.addResizeEventListener();

    this.setupVisibility();

    if (this.advertisementAvailable) {
      this.showAdvertisement();
    } else {
      this.showHome();
    }
  },

  beforeDestroy() {
    this.removeResizeEventListener();

    this.teardownVisibility();
  },

  methods: {
    showAdvertisement() {
      this.activeModule = MODULE_TYPE_ENUM.advertisement;
    },

    showHome() {
      this.activeModule = MODULE_TYPE_ENUM.home;
    },

    showPriceList(type) {
      this.priceListType = type;
      this.activeModule = MODULE_TYPE_ENUM.priceList;
    },

    addResizeEventListener() {
      window.addEventListener("resize", this.initializeWindowInnerHeight);
    },

    removeResizeEventListener() {
      window.removeEventListener("resize", this.initializeWindowInnerHeight);
    },

    initializeWindowInnerHeight() {
      this.windowInnerHeight = window.innerHeight + "px";
    },

    setupVisibility() {
      this.lastVisible = dayjs();

      pageLifecycle.addEventListener("statechange", this.updateVisibility);
    },

    teardownVisibility() {
      this.lastVisible = null;

      pageLifecycle.removeEventListener("stateChange", this.updateVisibility);
    },

    updateVisibility({ newState, oldState }) {
      if (
        oldState === PAGE_LIFECYCLE_STATE_ENUM.active &&
        newState !== PAGE_LIFECYCLE_STATE_ENUM.active
      ) {
        this.lastVisible = dayjs();

        return;
      }

      if (
        oldState !== PAGE_LIFECYCLE_STATE_ENUM.active &&
        newState === PAGE_LIFECYCLE_STATE_ENUM.active
      ) {
        const now = dayjs();
        const passedSeconds = now.diff(this.lastVisible, "s");

        if (passedSeconds >= REFRESH_TIMEOUT_S) {
          window.location.reload();
        } else {
          this.lastVisible = dayjs();
        }

        return;
      }
    },
  },
};
</script>

<style lang="scss">
@import "./scss/app";

*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  font-size: $size-rem;
  line-height: 1.15;
}

body {
  font-family: "effra", Helvetica, Arial, "Microsoft Yahei", "微软雅黑", STXihei,
    "华文细黑", sans-serif;
  font-weight: 400;
  color: $color-text;
  font-size: $size-font-1;
  line-height: 1.75;
  background: $color-white;
  transition: background-color 0.3s ease;

  @include mq($from: $breakpoint-tablet) {
    max-width: $breakpoint-tablet;
    margin: 0 auto;
  }
}
</style>
